import PropTypes from 'prop-types';
import { forwardRef, useMemo, useState } from 'react';
import useInvoiceTaxes from 'Hooks/useInvoiceTaxes';
import { cn } from 'Utils/cn';
import {
    useFloating,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    flip,
    useMergeRefs,
    autoUpdate,
    size,
    FloatingPortal,
} from '@floating-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import OutlineChevronDown from 'Components/Atoms/OutlineIcons/OutlineChevronDown';
import MultiselectInvoiceTaxesContent from './MultiselectInvoiceTaxesContent';
import MultiselectInvoiceTaxesValue from './MultiselectInvoiceTaxesValue';
import { useTranslation } from 'react-i18next';
import { DOCUMENTATION_LINKS } from 'Utils/constants';
import OutlineDocs from 'Components/Atoms/OutlineIcons/OutlineDocs';

const TAXES_ORDER = ['ESP_IVA', 'ESP_IRPF'];

const MultiselectInvoiceTaxes = forwardRef(function MultiselectInvoiceTaxes(props, ref) {
    const { value, disabled, onChange, className, error } = props;

    const { invoiceTaxes, isLoading } = useInvoiceTaxes(value);

    const optionsSelected = Array.isArray(value) ? value : [];

    const [open, setOpen] = useState(false);
    const { refs, floatingStyles, context, strategy, x, y } = useFloating({
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        placement: 'bottom-start',
        strategy: 'fixed',
        middleware: [
            flip(),
            size({
                apply({ _rects, elements, availableHeight }) {
                    Object.assign(elements.floating.style, {
                        maxHeight: `${Math.min(availableHeight - 20, 520)}px`,
                    });
                },
            }),
        ],
    });

    const { getFloatingProps, getReferenceProps } = useInteractions([
        useClick(context),
        useRole(context, { role: 'listbox' }),
        useDismiss(context),
    ]);
    const { t, i18n } = useTranslation('taxes');
    const docsLink = DOCUMENTATION_LINKS.TAXES_TYPES[i18n.language];

    const groups = useMemo(() => {
        const grouped = invoiceTaxes.reduce((acc, tax) => {
            if (!acc[tax.group]) {
                acc[tax.group] = [];
            }
            acc[tax.group].push(tax);
            return acc;
        }, {});

        return Object.entries(grouped)
            .map(([group, taxes]) => ({
                id: group,
                name: taxes[0]?.group_name || group,
                taxes: taxes,
            }))
            .sort((a, b) => {
                const indexA = TAXES_ORDER.indexOf(a.id);
                const indexB = TAXES_ORDER.indexOf(b.id);
                if (indexA === -1 && indexB === -1) return 0;
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
            });
    }, [invoiceTaxes]);

    const mergedRef = useMergeRefs([ref, refs.setReference]);

    if (isLoading || groups.length === 0) return <div className="h-10" />;

    return (
        <div className={cn('w-full', className)}>
            <button
                type="button"
                ref={mergedRef}
                {...getReferenceProps({
                    className: cn(
                        'overflow-hidden bg-transparent text-dark-blue w-full flex items-center-safe gap-0.5 h-10 px-1 text-left disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
                        '[&_svg]:pointer-events-none [&_svg]:shrink-0 shrink-0',
                        'focus-visible:outline-none',
                        error && 'ring-1 ring-danger',
                    ),
                    disabled,
                })}
            >
                <div className="flex-grow">
                    <MultiselectInvoiceTaxesValue taxIds={optionsSelected} allTaxes={invoiceTaxes} />
                </div>
                <OutlineChevronDown size={16} color="currentColor" />
            </button>

            <AnimatePresence>
                {open && (
                    <FloatingPortal>
                        <motion.div
                            ref={refs.setFloating}
                            style={{
                                ...floatingStyles,
                                position: strategy,
                                top: y ?? 0,
                                left: x ?? 0,
                            }}
                            className="w-[280px] overflow-x-hidden overflow-y-auto scrollbar-thin rounded-md bg-white shadow-sm ring-1 ring-black/5 focus:outline-none text-sm z-dropdown"
                            tabIndex={-1}
                            {...getFloatingProps()}
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.15, ease: 'easeOut' }}
                        >
                            <MultiselectInvoiceTaxesContent groups={groups} value={value} onChange={onChange} />
                            {!!docsLink && (
                                <div className="border-t bg-white border-gray-200 p-1.5 sticky bottom-0">
                                    <a
                                        key="action-docs-link"
                                        href={docsLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="flex items-center gap-2.5 text-left px-2 h-7 text-xs font-medium text-dark-blue no-underline"
                                    >
                                        <OutlineDocs size={16} />
                                        {t('docs-link')}
                                    </a>
                                </div>
                            )}
                        </motion.div>
                    </FloatingPortal>
                )}
            </AnimatePresence>
        </div>
    );
});

export default MultiselectInvoiceTaxes;

MultiselectInvoiceTaxes.propTypes = {
    disabled: PropTypes.bool,
    value: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    className: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    highlight: PropTypes.bool,
};
